import {Injectable} from '@angular/core';
import {HttpErrorResponse, HttpEvent, HttpHandler, HttpHeaders, HttpInterceptor, HttpParams, HttpRequest, HttpResponse} from '@angular/common/http';
import {Observable, ReplaySubject} from 'rxjs';
import {catchError, finalize, tap} from "rxjs/operators";
import {v4 as uuidv4} from 'uuid';

import {UserService} from "@app/core/user/user.service";
import {environment} from '../../../environments/environment';
import {HttpErrorMessageService} from '../../core/http/http-error-message.service';
import {GrowlService} from '../growl/growl.service';
import * as KeycloakAngular from "keycloak-angular";
import {Roles} from "@app/shared";
import {KeycloakSupportService} from "@app/core";

@Injectable()
export class KeycloakHttpInterceptorService implements HttpInterceptor {

  static pendingRequests = 0;
  static pendingRequestsStatusSub: ReplaySubject<boolean> = new ReplaySubject<boolean>(0);
  static pendingRequestsStatusObs: Observable<boolean> = KeycloakHttpInterceptorService.pendingRequestsStatusSub.asObservable();


  // get pendingRequests(): number {
  //   return this._pendingRequests;
  // }

  constructor(private growl: GrowlService, private httpError: HttpErrorMessageService, private user: UserService,
              private keycloakAngular: KeycloakAngular.KeycloakService,
              private keycloakSupport: KeycloakSupportService) {
  }

  morePendingRequest() {
    KeycloakHttpInterceptorService.pendingRequests++;
    console.debug(`httpInterceptor:more pendingRequests = ${KeycloakHttpInterceptorService.pendingRequests}`);

    if (1 === KeycloakHttpInterceptorService.pendingRequests) {
      KeycloakHttpInterceptorService.pendingRequestsStatusSub.next(true);
    }
  }

  lessPendingRequest() {
    if (KeycloakHttpInterceptorService.pendingRequests > 0) {
      KeycloakHttpInterceptorService.pendingRequests--;
      console.debug(`httpInterceptor:less pendingRequests = ${KeycloakHttpInterceptorService.pendingRequests}`);
    } else {
      console.warn("httpInterceptor:less pendingRequests is already 0");
    }

    if (0 === KeycloakHttpInterceptorService.pendingRequests) {
      KeycloakHttpInterceptorService.pendingRequestsStatusSub.next(false);
    }
  }

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {

    let isSpinnerOn = true;

    //Check if spinner should be activated based on the spinnerUrls
    if (environment['spinnerUrls']) {
      for (let url of environment['spinnerUrls']) {
        let regex = new RegExp(url.urlRegex);

        if (regex.test(req.url)) {
          isSpinnerOn = false;
          console.debug(`bypassing spinner for ${req.url}`);
          break;
        }
      }
    }

    // add pending http request; reduce it when http response comes back
    if (isSpinnerOn) {
      this.morePendingRequest();
    }

    const reqWithNewHeader: HttpRequest<any> = this.setHeader(req);
    return next.handle(reqWithNewHeader)
      .pipe(tap((event: HttpEvent<any>) => {
        if (event instanceof HttpResponse) {
          console.log(event);
        }
      }))
      .pipe(catchError((httpErrorResponse: HttpErrorResponse) => {
        if (!this.ifExcludedUrl(req)) {
          this.growl.add("error", this.httpError.getErrorMessage(httpErrorResponse, req), false, req);
        }
        //this.growl.add("error", this.httpError.getErrorMessage(httpErrorResponse, req), false, req);
        return Observable.throw(httpErrorResponse);
      }))
      .pipe(finalize(() => {
        if (this.isKeycloakOrDataServiceUrl(reqWithNewHeader)) {
          console.log("HTTP to either Keycloak or DataServices, publish ssoIdle...");
          this.keycloakSupport.resetSsoIdle();
        }
        if (isSpinnerOn) {
          this.lessPendingRequest();
        } else {
          console.debug(`bypassing lessPendingRequest() for ${req.url}`);
        }
      }));
  }

  setHeader(req: HttpRequest<any>): HttpRequest<any> {
    // return any pre-defined list that require no alteration
    for (let immutableUrl of environment['immutableUrls']) {
      if (req.url.includes(immutableUrl['url'])) {
        console.debug(`setHeader: Bypassing immutable url pattern = ${immutableUrl['url']} req.url = ${req.url}`);
        return req;
      }
    }

    let keycloakAngularInstance = this.keycloakAngular.getKeycloakInstance();
    if (!keycloakAngularInstance.token || !keycloakAngularInstance.realm) {
      console.warn('KeycloakHttpInterceptorService setHeader: No keycloakAngularInstance token or realm. Returning request unchanged...');
      return req;
    }

    // check and set necessary headers, params, and url to call onelynk dataservices
    let newReq;
    let headers = new HttpHeaders();
    let params = new HttpParams();

    // copy the headers and params
    Object.assign(headers, req.headers);
    Object.assign(params, req.params);

    if (!req.headers.has('Content-type')) {
      headers = headers.append('Content-type', 'application/json');
    }

    if (!req.headers.has('Authorization')) {
      headers = headers.append('Authorization', 'Bearer ' + keycloakAngularInstance.token);
      params = params.append('realm', keycloakAngularInstance.realm);
    }

    //Check if the browser in use is IE or EDGE and add a Cace-Control - no-cache to the header ,so that IE doesnt cache the requests. IE/EDGE tends to cache the GET requests
    if (/msie\s|trident\/|edge\//i.test(window.navigator.userAgent)) {

      headers = headers.append('Cache-Control', 'no-cache');
      headers = headers.append('pragma', 'no-cache');

    }

    //Set an unique number as transaction Id, for traceability in the application logs
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call
    headers = headers.append('transactionId', uuidv4());

    // set the url, header and params
    let url: string;
    if (req.url.includes('/admin/realms/')) {
      // sso-3990
      if (this.user && this.user.hasRole[Roles.CSRAdmin]) {
        url = environment.keycloakConfig.authServerUrlForCsr + req.url;
      } else {
        url = environment.keycloakConfig.authServerUrl + req.url;
      }
    } else {
      // sso-3990
      if (this.user && this.user.hasRole[Roles.CSRAdmin]) {
        url = environment.restAPIurlForCSR + req.url;
      } else {
        url = environment.restAPIurl + req.url;
      }
    }

    //TODO: Enable class to plain transformation so that @Exclude propertie will not be included in payload
    //let updatedBody = classToPlain(req.body);
    newReq = req.clone({
      url: url,
      headers: headers,
      params: params
    });

    return newReq;
  }

  ifExcludedUrl(req: HttpRequest<any>) {
    // exclude realm validation check call to keycloak
    let splitted = req.url.toString().split("/auth/realms/");
    if (splitted.length == 2 && !splitted[1].includes("/")) {
      console.log("Ignoring exception for realm check: " + req.url);
      return true;
    }

    for (let excludeUrl of environment['excludeErrorMessageByInterceptorUrls']) {
      if (req.url.includes(excludeUrl['url']) && req.method == excludeUrl['method']) {
        console.log("Ignoring exception for " + req.url);
        return true;
      }
    }
    return false;
  }

  isKeycloakOrDataServiceUrl(req: HttpRequest<any>) {
    return req.url.includes(environment.restAPIurl) || req.url.includes(environment.restAPIurlForCSR) ||
      req.url.includes(environment.keycloakConfig.authServerUrl) || req.url.includes(environment.keycloakConfig.authServerUrlForCsr);
  }
}
